import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useContext } from 'react';
import { useMatch, Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { useFeatureFlags } from 'libs.flags.react';
import { LocaleContext, Spinner, Tabs, useDialog, useLocalizeMessage, useToastNotification, } from 'libs.nucleus';
import { PageHeader, useBooleanState, PageWrapper } from 'libs.react';
import { useTabNavigation } from 'libs.react_internal';
import { ParticipantCaregivers } from '../../components/participant_caregivers/participant_caregivers.components';
import { ParticipantDetails } from '../../components/participant_details/participant_details.component';
import { ParticipantDocuments } from '../../components/participant_documents/participant_documents.component';
import { ParticipantResponses } from '../../components/participant_responses';
import { ParticipantVisits } from '../../components/participant_visits/participant_visits.component';
import { PiOversightDetailsModal } from '../../components/pi_oversight_details/pi_oversight_details_modal.component';
import { PIOversightSummary } from '../../components/pi_oversight_summary/pi_oversight_summary.component';
import { SITE_FEATURE_FLAGS } from '../../constants/feature_flags.constants';
import { MIN_AXON_VERSION_FOR_RESPONSES } from '../../constants/versions.constants';
import { SiteContext } from '../../contexts/site';
import { getApiErrorMessage, getApiInstance } from '../../helpers/api.helpers';
import { appendSiteIdToPath } from '../../helpers/links.helpers';
import { getParticipantDetails, getParticipantNumber, isParticipantACaregiver, isParticipantAuthenticated, shouldShowTelevistButton, } from '../../helpers/participants.helpers';
import { extractClassicAppsBaseUrl } from '../../helpers/study.helpers';
import { meetsMinimumVersion } from '../../helpers/version.helpers';
import { useCurrentEnvironment } from '../../hooks/use_current_environment';
import { useLumosAuth } from '../../hooks/use_lumos_auth';
import { useOrgContext } from '../../hooks/use_org_context';
import { usePIOversightAccess } from '../../hooks/use_pi_oversight_access';
import { usePIOversightFooter } from '../../hooks/use_pi_oversight_footer';
import { EParticipantInviteStatus } from '../../types/participants.type';
import { LUMOS_PERMISSIONS } from '../../types/permissions.types';
const PI_OVERSIGHT_DETAILS_VIEW_PARAM = 'oversight-details';
export const ParticipantDetailsPage = () => {
    const translate = useLocalizeMessage();
    const { environment } = useCurrentEnvironment();
    const { site } = useContext(SiteContext);
    const currentLocale = useContext(LocaleContext);
    const org = useOrgContext();
    const [isBusy, setIsbusy, unsetIsBusy] = useBooleanState(false);
    const { isUserPermitted, isSiteInvestigator } = useLumosAuth();
    const { isFeatureEnabled } = useFeatureFlags();
    const isPIOversightEnabled = usePIOversightAccess({ requirePIRole: false });
    const { renderPIOversightFooter } = usePIOversightFooter();
    const navigate = useNavigate();
    const { hash } = useLocation();
    const match = useMatch('/site/study/:studyId/participants/:participantId/:view?');
    const participantId = match?.params.participantId;
    const { addNotification } = useToastNotification();
    const dialog = useDialog();
    const basePathName = `/site/study/${environment?.id}/participants/${participantId}`;
    const queryClient = useQueryClient();
    const [activeTab, setActiveTab] = useTabNavigation([
        '#visits',
        '#details',
        '#responses',
        '#documents',
        '#caregivers',
        '#audit-history',
    ]);
    const getParticipantData = async () => {
        if (!environment) {
            return {};
        }
        return await getParticipantDetails({
            environment,
            siteId: site?._id ?? '',
            participantId: participantId ?? '',
            locale: currentLocale,
        });
    };
    const handleInviteCancel = async () => {
        const ok = await dialog.open({
            title: translate('label.participant_details.cancel_invite'),
            body: translate('prompt.participant_details.cancel_confirmation'),
            actionButtons: [
                {
                    isDestructive: true,
                    label: translate('action.participant_details.cancel_invite_accept'),
                    dataTestId: 'cancel-invite-accept',
                },
                { label: translate('action.participant_details.cancel_invite_decline'), dataTestId: 'cancel-invite-decline' },
            ],
        });
        if (ok && environment) {
            try {
                setIsbusy();
                await getApiInstance(environment).post('/routes/cancel_invite', {
                    c_site: site?._id,
                    c_public_user: participantId,
                });
                addNotification({
                    title: '',
                    subtitle: translate('status.participant_details.invite_canceled'),
                    type: 'success',
                });
                queryClient.invalidateQueries({
                    queryKey: ['participantDetails', participantId],
                });
            }
            catch (e) {
                const message = getApiErrorMessage(e);
                addNotification({
                    title: '',
                    subtitle: message ?? translate('error.generic'),
                    type: 'error',
                });
            }
            finally {
                unsetIsBusy();
            }
        }
    };
    const onHandleNavigationToTelevist = () => {
        if (!environment) {
            return '';
        }
        const televisitUrl = appendSiteIdToPath(`${extractClassicAppsBaseUrl(environment.cortexURL, 'site')}/participants/${participantId}?orgCode=${environment.code}`, site?._id ?? '');
        window.open(televisitUrl, '_blank')?.focus();
    };
    const handleOpenPIOversightDetailsModal = () => {
        navigate({
            pathname: `${basePathName}/${PI_OVERSIGHT_DETAILS_VIEW_PARAM}`,
            hash: hash || '',
        });
    };
    const handleClosePIOversightDetailsModal = () => {
        navigate({
            pathname: basePathName,
            hash: hash || '',
        });
    };
    const { data: participantData, isLoading } = useQuery({
        queryKey: ['participantDetails', participantId, currentLocale],
        queryFn: getParticipantData,
        enabled: true,
    });
    const breadcrumbsPages = [
        { name: translate('generic.participants'), path: `/site/study/${environment?.id}/participants` },
        { name: participantData?.c_number || translate('label.participant_details.detail'), path: '' },
    ];
    const hasEconsent = org?.c_requires_econsent_field;
    const isCaregiverFeatureEnabled = (org?.c_caregiver_enabled ?? false) && !isParticipantACaregiver(participantData);
    const isResponsesTabEnabled = isFeatureEnabled(SITE_FEATURE_FLAGS.PARTICIPANT_RESPONSES_TAB) &&
        meetsMinimumVersion({
            currentVersion: org?.axon__version,
            minimumVersion: MIN_AXON_VERSION_FOR_RESPONSES,
        });
    const headerOptions = [];
    const canCancelInvite = isUserPermitted(LUMOS_PERMISSIONS.PARTICIPANTS_CANCEL_INVITE) &&
        !isParticipantAuthenticated(participantData) &&
        participantData?.c_invite !== EParticipantInviteStatus.EXPIRED;
    //  If participant isnt authenticated and invite is not expired, show cancel invite button
    if (canCancelInvite) {
        headerOptions.push({
            name: translate('action.participant_details.cancel_invite'),
            onClick: handleInviteCancel,
            variant: 'secondary',
            dataTestId: 'cancel-invite-button',
        });
    }
    if (shouldShowTelevistButton(participantData, org)) {
        headerOptions.push({
            name: translate('action.participant_details.redirect_to_TV'),
            onClick: onHandleNavigationToTelevist,
            variant: 'primary',
            dataTestId: 'televist-button',
            iconName: 'externalLink',
        });
    }
    const tabItems = [
        {
            content: _jsx(ParticipantVisits, { participantNumber: participantData?.c_number || '' }, 'participant-visits'),
            label: translate('generic.visits'),
            dataTestId: 'participant-visits-tab',
        },
        {
            content: _jsx(ParticipantDetails, { participant: participantData, participantDetailsLoading: isLoading }),
            label: translate('generic.details'),
        },
        {
            content: _jsx(ParticipantResponses, { participantId: participantId || '' }, 'participant-responses'),
            label: translate('generic.responses'),
            dataTestId: 'participant-responses-tab',
            hidden: !isResponsesTabEnabled,
        },
        {
            content: _jsx(ParticipantDocuments, { participant: participantData }),
            label: translate('generic.consent'),
            hidden: !hasEconsent,
        },
        {
            content: _jsx(ParticipantCaregivers, { participant: participantData, participantDetailsLoading: isLoading }),
            label: translate('generic.caregivers'),
            hidden: !isCaregiverFeatureEnabled,
        },
    ];
    // If the user is not a site investigator and is trying to access the
    // PI oversight details page, navigate to the participant details page
    if (match?.params.view === PI_OVERSIGHT_DETAILS_VIEW_PARAM && (!isPIOversightEnabled || !isSiteInvestigator)) {
        return _jsx(Navigate, { to: basePathName, state: { hash: hash || '' } });
    }
    return (_jsxs(PageWrapper, { children: [_jsx(PageHeader, { breadcrumbPages: breadcrumbsPages, title: participantData ? getParticipantNumber(participantData) : '', options: headerOptions, LinkComponent: Link }), isBusy && _jsx(Spinner, { wrapper: 'full' }), isPIOversightEnabled && (_jsx(PIOversightSummary, { participantId: participantId ?? '', renderFooterNode: ({ reviewBundle }) => renderPIOversightFooter(reviewBundle, handleOpenPIOversightDetailsModal) })), _jsx(Tabs, { tabItems: tabItems, defaultIndex: activeTab, onTabChange: setActiveTab }), isPIOversightEnabled && (_jsx(PiOversightDetailsModal, { isOpen: match?.params.view === PI_OVERSIGHT_DETAILS_VIEW_PARAM, participantId: participantId ?? '', onClose: handleClosePIOversightDetailsModal }))] }));
};
